import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ClipboardCheckFillIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useGetPricing } from '@/api/gen/pricing/pricing.gen';
import { useGetUserPlanAvailability } from '@/api/gen/quiz-plan-payment/quiz-plan-payment.gen';
import { useUser } from '@/lib/auth';
import { useCountryCode } from '@/shared/hooks/useCountryCode';
import { useTrialEndDiscount } from '@/shared/plan/useTrialEndDiscount';
import { QuizQuoteForm } from './components/QuizQuoteForm';
import { QuotePreviewAside } from './components/QuotePreviewAside';
import { parseQuoteSegment, QUOTE_SEGMENT_QUERY_KEY, type QuoteSegment } from './consts/quizQuote.const';
import { useQuizQuoteForm } from './hooks/useQuizQuoteForm';
import { useQuoteSubmit } from './hooks/useQuoteSubmit';
import { type TrialEndQuoteDiscount } from './utils/quoteCalculator';
import { parseQuotePricingPolicy } from './utils/quotePricingPolicy';

/**
 * 체험 종료 할인 견적 모드 (PRD 솔루션 6, S6-04/05).
 *
 * `?discount=trial` 은 모달·배너에서 온 진입 표식일 뿐이고, 할인을 켤지는 서버 자격이
 * 결정한다 — 쿼리만 보고 켜면 자격 없는 사용자에게 없는 할인가를 보여주게 된다.
 */
function useTrialEndQuoteDiscount(): TrialEndQuoteDiscount | null {
  const discount = useTrialEndDiscount();
  const isEligible = discount.status === 'active' || discount.status === 'urgent';
  if (!isEligible || !discount.windowEnd) return null;
  return {
    discountRate: discount.discountRate,
    maxUserCount: discount.maxUserCount,
    maxMonthCount: discount.maxMonthCount,
    windowEnd: discount.windowEnd.toISOString()
  };
}

/**
 * 견적서 발행 페이지(Phase 1a, Figma 13614-55850).
 *
 * External Platform: 견적은 normal Quiz 세일즈 플로우다. LTI/Google Classroom 은
 * assignment-scoped 라 이 진입점(프라이싱) 자체가 노출되지 않으므로 별도 분기 없음.
 */
export function QuotePageView() {
  const router = useRouter();
  const {
    isKR,
    isReady: isCountryCodeReady
  } = useCountryCode();
  useEffect(() => {
    if (!router.isReady || !isCountryCodeReady || isKR) return;
    void router.replace('/pricing');
  }, [isCountryCodeReady, isKR, router]);
  if (!router.isReady || !isCountryCodeReady || !isKR) {
    return null;
  }
  const segment = parseQuoteSegment(router.query[QUOTE_SEGMENT_QUERY_KEY]);
  return <QuotePageContent segment={segment} data-sentry-element="QuotePageContent" data-sentry-component="QuotePageView" data-sentry-source-file="QuotePageView.tsx" />;
}
function QuotePageContent({
  segment
}: {
  segment: QuoteSegment;
}) {
  const trialEndDiscount = useTrialEndQuoteDiscount();
  const methods = useQuizQuoteForm(segment, trialEndDiscount !== null);
  const {
    isAuthenticated,
    user
  } = useUser();
  const needsPromotionEligibility = isAuthenticated && user?.planType === 'FREE';
  const {
    data: policy = null
  } = useGetPricing({
    countryCode: 'KR'
  }, {
    query: {
      select: parseQuotePricingPolicy,
      retry: 0,
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const availability = useGetUserPlanAvailability({
    query: {
      enabled: needsPromotionEligibility,
      retry: 0,
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const promotion = availability.data?.data.firstPurchasePromotion;
  const isBasicFirstPurchaseEligible = promotion?.eligible && promotion.targetPlanType === 'BASIC';
  const firstPurchasePromotion = isBasicFirstPurchaseEligible ? {
    discountRate: promotion.discountRate,
    maxDiscountMonths: promotion.maxDiscountMonths ?? null
  } : null;
  const submit = useQuoteSubmit({
    setError: methods.setError,
    clearErrors: methods.clearErrors,
    policy,
    firstPurchasePromotion,
    trialEndDiscount
  });
  return <div className="flex min-h-screen justify-center bg-neutral px-4 py-10" data-sentry-component="QuotePageContent" data-sentry-source-file="QuotePageView.tsx">
      <div className="w-full max-w-[1000px]">
        <div className="mb-xl flex items-center gap-2xs">
          <ClipboardCheckFillIcon size="lg" fill={ICON_COLOR.default} data-sentry-element="ClipboardCheckFillIcon" data-sentry-source-file="QuotePageView.tsx" />
          <h1 className="text-heading-lg-extrabold text-default">
            견적서 발행
          </h1>
        </div>

        <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="QuotePageView.tsx">
          <form noValidate onSubmit={methods.handleSubmit(submit)} className="flex flex-col gap-lg tablet-lg:flex-row tablet-lg:items-start tablet-lg:gap-[40px]">
            <div className="w-full tablet-lg:min-w-0 tablet-lg:flex-1">
              <QuizQuoteForm policy={policy} firstPurchasePromotion={firstPurchasePromotion} trialEndDiscount={trialEndDiscount} data-sentry-element="QuizQuoteForm" data-sentry-source-file="QuotePageView.tsx" />
            </div>
            <div className="w-full tablet-lg:w-[400px] tablet-lg:shrink-0">
              <QuotePreviewAside policy={policy} firstPurchasePromotion={firstPurchasePromotion} trialEndDiscount={trialEndDiscount} data-sentry-element="QuotePreviewAside" data-sentry-source-file="QuotePageView.tsx" />
            </div>
          </form>
        </FormProvider>
      </div>
    </div>;
}